"use client";

import React, { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { ArrowRight, Sparkles } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const galleryData = [{
  id: 1,
  type: "image",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1599594/img/gallery_tesla_ceramic.png",
  label: "Hail Damage Restoration",
  className: "md:col-span-1 md:row-span-1"
}, {
  id: 2,
  type: "slider",
  beforeSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1599594/img/gallery_paint_before.png",
  afterSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1599594/img/gallery_paint_after.png",
  label: "Structural Realignment - 2023 Jayco",
  className: "md:col-span-2 md:row-span-2"
}, {
  id: 3,
  type: "image",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1599594/img/gallery_bmw_tint.png",
  label: "Off-Road Solar Integration",
  className: "md:col-span-1 md:row-span-2"
}, {
  id: 4,
  type: "image",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1599594/img/gallery_audi_detail.png",
  label: "Interior Electrical Refit",
  className: "md:col-span-1 md:row-span-1"
}, {
  id: 5,
  type: "image",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1599594/img/gallery_gwagon_ppf.png",
  label: "Complete Chassis Overhaul",
  className: "md:col-span-2 md:row-span-1"
}, {
  id: 6,
  type: "image",
  src: "https://climbing-camel.10web.cloud/wp-content/uploads/2026/03/post_choosing-the-right-caravan-repair-service-in-sydney.webp?v=8vsojdw07k8",
  label: "Premium Exterior Respray",
  className: "md:col-span-2 md:row-span-1"
}];
export default function PrecisionInEveryDetail() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  return <section data-section-id="135148" id="workshop-transformations" className="bg-background text-foreground py-24 px-4 md:px-8 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div ref={headerRef} className={`mb-16 text-center transition-all duration-1000 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-secondary/10 border border-border mb-6">
            <Sparkles className="w-4 h-4 text-secondary" />
            <span className="text-xs font-mono font-bold text-secondary uppercase tracking-widest">
              Repair Gallery
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-sans font-extrabold tracking-tight mb-6 text-primary">
            Workshop Transformations
          </h2>
          <p className="text-lg text-muted-foreground font-sans max-w-2xl mx-auto leading-relaxed">
            Experience the precision of South Windsor's leading caravan restoration specialists. We restore structural integrity and aesthetic finish to every vehicle that enters our workshop.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6 auto-rows-[300px] md:auto-rows-[350px]">
          {galleryData.map((item, i) => {
          const {
            ref: itemRef,
            isVisible: isItemVisible
          } = useRevealOnScroll({
            threshold: 0.05
          });
          return <div key={item.id} data-index={i} ref={itemRef} className={`relative rounded-lg overflow-hidden ring-1 ring-inset ring-border group bg-card shadow-sm transition-all duration-700 transform ${item.className} ${isItemVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
            transitionDelay: `${i * 100}ms`
          }}>
                {item.type === "image" ? <>
                    <img src={item.src} alt={item.label} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none flex items-end p-6">
                      <span className="text-sm md:text-base font-sans font-semibold text-primary-foreground translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                        {item.label}
                      </span>
                    </div>
                  </> : <div className="@container w-full h-full relative">
                    <ResizablePanelGroup direction="horizontal">
                      <ResizablePanel defaultSize={50} className="relative overflow-hidden">
                        <img src={item.beforeSrc} className="absolute top-0 left-0 h-full w-[100cqw] max-w-none object-cover object-left grayscale-[0.4]" alt="Before Restoration" />
                        <div className="absolute top-4 left-4 bg-background/90 backdrop-blur-md px-3 py-1.5 rounded-md text-[10px] font-mono font-bold tracking-widest text-foreground border border-border shadow-sm">
                          BEFORE
                        </div>
                      </ResizablePanel>
                      <ResizableHandle withHandle className="w-1.5 bg-secondary hover:bg-accent transition-colors z-10 data-[state=hover]:w-2 data-[state=drag]:w-2 data-[state=drag]:bg-accent" />
                      <ResizablePanel defaultSize={50} className="relative overflow-hidden">
                        <img src={item.afterSrc} className="absolute top-0 right-0 h-full w-[100cqw] max-w-none object-cover object-right" alt="After Restoration" />
                        <div className="absolute top-4 right-4 bg-accent/90 backdrop-blur-md px-3 py-1.5 rounded-md text-[10px] font-mono font-bold tracking-widest text-accent-foreground shadow-sm">
                          AFTER
                        </div>
                      </ResizablePanel>
                    </ResizablePanelGroup>
                    <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-primary/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20 pointer-events-none flex items-end">
                      <span className="text-sm md:text-base font-sans font-semibold text-primary-foreground translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                        {item.label}
                      </span>
                    </div>
                  </div>}
              </div>;
        })}
        </div>

        <div className="mt-16 flex justify-center">
          <Button asChild variant="outline" size="lg" className="border-border hover:bg-secondary/5 hover:text-primary group font-sans font-bold">
            <Link to="/StartYourQuotePage">
              Start Your Quote
              <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}